import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import ShareLinkPanel from '../components/ShareLinkPanel';
import ReturnLinkHint from '../components/ReturnLinkHint';
import { getSessionStatus, ApiError } from '../lib/api';
import { safeLocalStorage } from '../lib/safeStorage';

export default function WaitingPage() {
  const { sessionId = '', partner = 'A' } = useParams<{ sessionId: string; partner: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Token handling ─────────────────────────────────────────────────
  // Accept token from ?t= URL param (cross-browser return link) and save to localStorage
  const tokenFromUrl = searchParams.get('t');
  if (tokenFromUrl) {
    safeLocalStorage.setItem(`quiz_token_${sessionId}`, tokenFromUrl);
  }

  const partnerToken = safeLocalStorage.getItem(`quiz_token_${sessionId}`);

  const [status, setStatus] = useState<{
    partnerAName: string;
    partnerBName: string;
    partnerACompleted: boolean;
    partnerBCompleted: boolean;
    bothCompleted: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean token from URL after extraction
  useEffect(() => {
    if (searchParams.has('t')) {
      searchParams.delete('t');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Build share URL from stored token (Partner A may have stored it)
    const partnerBToken = safeLocalStorage.getItem(`quiz_token_b_${sessionId}`);
    if (partnerBToken) {
      const appUrl = (window.location.origin || import.meta.env.VITE_APP_URL).replace(/\/+$/, '');
      setShareUrl(`${appUrl}/quiz/${sessionId}/B?token=${partnerBToken}`);
    }

    async function poll() {
      try {
        const data = await getSessionStatus(sessionId);
        setStatus({
          partnerAName: data.partnerAName,
          partnerBName: data.partnerBName,
          partnerACompleted: data.partnerACompleted,
          partnerBCompleted: data.partnerBCompleted,
          bothCompleted: data.bothCompleted,
        });
        setError(null);

        if (data.bothCompleted) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          navigate(`/results/${sessionId}`);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setError('This session has expired or does not exist.');
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
        // Silently retry on network errors
      }
    }

    poll();
    intervalRef.current = setInterval(poll, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessionId, navigate]);

  // ── Error state ────────────────────────────────────────────────────

  if (error) {
    return (
      <AppShell title="Waiting...">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-5xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-white mb-3">
            Session not found
          </h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/start')}
            className="px-6 py-3 gradient-cta text-white font-semibold rounded-xl transition-all"
          >
            Start a new quiz
          </button>
        </div>
      </AppShell>
    );
  }

  // ── Waiting state ──────────────────────────────────────────────────

  const otherPartnerName =
    partner === 'A' ? (status?.partnerBName ?? 'your partner') : (status?.partnerAName ?? 'your partner');

  const theyCompleted = partner === 'A' ? status?.partnerBCompleted : status?.partnerACompleted;

  return (
    <AppShell title="Waiting...">
      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-6 animate-pulse">⏳</div>

          <h1 className="text-2xl font-bold text-white mb-3">
            {theyCompleted
              ? 'Your partner is done!'
              : `Waiting for ${otherPartnerName}...`}
          </h1>

          <p className="text-slate-400">
            {theyCompleted
              ? 'Both of you are done! Redirecting to results...'
              : "We'll show your results as soon as both of you have finished."}
          </p>
        </div>

        {/* Status indicators */}
        <div className="bg-purple-card rounded-xl border border-white/8 p-4 space-y-3">
          <StatusRow
            label={partner === 'A' ? 'You' : (status?.partnerAName ?? 'Partner A')}
            completed={!!status?.partnerACompleted}
          />
          <StatusRow
            label={partner === 'B' ? 'You' : (status?.partnerBName ?? 'Partner B')}
            completed={!!status?.partnerBCompleted}
          />
        </div>

        {/* Share link (Partner A only) */}
        {partner === 'A' && shareUrl && (
          <ShareLinkPanel variant="light" shareUrl={shareUrl} partnerBName={status?.partnerBName ?? 'your partner'} />
        )}

        <ReturnLinkHint
          url={`${window.location.origin}/waiting/${sessionId}/${partner}${partnerToken ? `?t=${partnerToken}` : ''}`}
          note="Bookmark this link to check back later."
        />
      </div>
    </AppShell>
  );
}

function StatusRow({ label, completed }: { label: string; completed: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-slate-300">{label}</span>
      <span
        className={`text-sm font-medium ${
          completed ? 'text-primary' : 'text-slate-400'
        }`}
      >
        {completed ? '✓ Done' : '⏳ In progress'}
      </span>
    </div>
  );
}
